/**
 * API client for the tenant's service credentials
 * (apps/api-controller/src/modules/service-credentials/routes.ts).
 *
 * The list never carries a secret or a hash, only the token prefix an
 * operator matches against a config file. Keep it that way: this type is what
 * the Service credentials page renders, field by field.
 */

import { apiFetch } from './api';
import { canAct } from './access';
import type { Caller } from '@/generated/persona/discovery-access';

export const SERVICE_CREDENTIAL_PURPOSES = ['decision_gateway', 'dsar_connector', 'core_provider', 'web_proxy'] as const;
export type ServiceCredentialPurpose = (typeof SERVICE_CREDENTIAL_PURPOSES)[number];

export type ServiceCredentialStatus = 'active' | 'rotating' | 'expired' | 'revoked';

export interface ServiceCredential {
  id: string;
  tenantId: string;
  purpose: ServiceCredentialPurpose;
  instanceLabel: string;
  tokenPrefix: string;
  status: ServiceCredentialStatus;
  createdBy: string;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  /** The revoker's name when the actor is one of the tenant's users; null for a system actor. */
  revokedByName: string | null;
  revocationReason: string | null;
  replacedById: string | null;
}

/** RevokeServiceCredentialSchema: trimmed, 1 to 250 characters. */
export const REVOKE_REASON_MAX = 250;

export async function listServiceCredentials(): Promise<ServiceCredential[]> {
  const res = await apiFetch<{ credentials: ServiceCredential[] }>('/v1/admin/service-credentials');
  return res.credentials;
}

export async function revokeServiceCredential(id: string, reason: string): Promise<ServiceCredential> {
  const res = await apiFetch<{ record: ServiceCredential }>(
    `/v1/admin/service-credentials/${encodeURIComponent(id)}/revoke`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
  return res.record;
}

/**
 * May the caller revoke a credential of this purpose? A copy of the rule in
 * routes.ts (header table, decision 89, and `requireForPurpose`): a
 * dsar_connector credential may be revoked by either the DPO's
 * dsar:connector_credential:write or the IT Admin's foundational:connector:write,
 * because stopping access is always safe; every other purpose is configuration
 * and needs foundational:connector:write. Change both together. This only
 * decides whether the button is offered; the route still decides, and its 403
 * is shown on the page.
 */
export function mayRevokeServiceCredential(purpose: ServiceCredentialPurpose, caller: Caller | null): boolean {
  if (canAct('foundational:connector:write', caller)) return true;
  return purpose === 'dsar_connector' && canAct('dsar:connector_credential:write', caller);
}

/**
 * A revoked row is final. An expired one already authenticates nothing. A
 * rotating row is the old half of a rotation and still works until its grace
 * ends, so it can be stopped early.
 */
export function isRevocable(status: ServiceCredentialStatus): boolean {
  return status === 'active' || status === 'rotating';
}
